// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { observable, computed, action, makeObservable, runInAction } from 'mobx';

import CommentsService from '@shared/services/comments';
import Comment, { CommentDTO } from '@shared/models/comment';
import { Id } from '@shared/types/common';

export default class CommentsStore {
  private service = new CommentsService();
  private urlPrefix: string;
  @observable _list: Array<Comment> = [];
  @observable loading: { [key: string]: boolean } = {
    list: false,
  };

  constructor({ urlPrefix }: { urlPrefix: string }) {
    this.urlPrefix = `/${urlPrefix}`;
    makeObservable(this);
  }

  // property to read the list of comments.
  @computed get list() {
    return this._list;
  }

  // Return the endpoint to manipulate comments.
  private getUrl(id: Id) {
    return `${this.urlPrefix}/${id}/comments`;
  }

  // Load list of comments from portal-api into a class var.
  // Note: must call this first before accessing the 'list' property.  Otherwise, the list may be stale.
  @action getList = async (id: Id) => {
    const url = this.getUrl(id);
    this.loading.list = true;

    try {
      const list = await this.service.getList(url);
      runInAction(() => {
        this._list = list;
        this.loading.list = false;
      });
    } 
    catch(err) {
      console.log("****** Exception in CommentStore getList:", err);
      runInAction(() => {
        this.loading.list = false;
      });
    }
  }

  // Should this be @action and async?
  send = (id: Id, comment: Partial<CommentDTO> & {exceptionCode: string}) => {
    const url = this.getUrl(id);

    return this.service.send(url, comment);
  }

  // Clear only the list property not the DB record.
  @action reset = () => {
    this._list = [];
  }
}